import { useRef } from "react";
import { SortOrder } from "antd/es/table/interface";
import { Space, Tag, notification } from "antd";
import { LinkOutlined, CopyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { IInvoice } from "../../../../interfaces";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { formatCurrency, formatPoints } from "../../../../utils/Masks";
import StatusTag from "../../components/StatusTag";
import useModules from "../../../../hooks/useModules";
import { useGetColumnSearchProps } from "../../../../hooks/FilterTable/stringClearFiltros";
import { useGetColumnSearchPropsData } from "../../../../hooks/FilterTable/date";
import TableActionButton from "../../../../components/TableActionButton";
import { getLinkInvoice, normalizeLink, verifyLinkIsValid } from "../../../../utils/Functions";
import useAuth from "../../../../hooks/useAuth";

interface ITableData extends IInvoice {
    key: number;
}

export default function useInvoices() {
    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const { hasRewardModule } = useModules();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role?.id === 1;

    const columns = [
        ...(isAdmin ? [{
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 75,
            align: 'center' as const,
            render: (id: number) => (
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/nota/detalhes/${id}`);
                    }}
                    style={{ color: '#1677ff', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                    #{id}
                </span>
            ),
        }] : []),
        {
            title: 'Empresa',
            dataIndex: 'company',
            key: 'company',
            minWidth: 200,
            sorter: (a: ITableData, b: ITableData) => a?.company?.localeCompare(b?.company || '')!,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({ dataIndex: 'company', ref: refFuncaoLimparFiltrosString }),
            render: (comp: string) => (
                <span style={{ fontWeight: 500 }}>
                    {comp || '--'}
                </span>
            ),
        },
        {
            title: 'Valor',
            dataIndex: 'value',
            key: 'value',
            width: 120,
            align: 'right' as const,
            render: (record: number) => (
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {record ? `R$ ${formatCurrency(record)}` : 'R$ 0,00'}
                </span>
            ),
            sorter: (a: ITableData, b: ITableData) => (a?.value || 0) - (b?.value || 0),
            showSorterTooltip: false,
        },
        ...(hasRewardModule ? [{
            title: 'Pontos',
            dataIndex: 'reward_points',
            key: 'reward_points',
            width: 100,
            align: 'center' as const,
            render: (record: number) => (
                record ? <Tag color="purple" style={{ margin: 0, borderRadius: 4 }}>{formatPoints(record)}</Tag> : '--'
            ),
            sorter: (a: ITableData, b: ITableData) => (a?.reward_points || 0) - (b?.reward_points || 0),
            showSorterTooltip: false,
        }] : []),
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            align: 'center' as const,
            sorter: (a: ITableData, b: ITableData) => a.status.localeCompare(b.status),
            showSorterTooltip: false,
            filters: [
                { text: 'Concluído', value: 'Concluído' },
                { text: 'Com erro', value: 'Erro' },
                { text: 'Alerta', value: 'Alerta' },
            ],
            onFilter: (value: any, record: ITableData) => record.status === value,
            render: (_: any, { id, status, message }: ITableData) => (
                <StatusTag
                    status={status}
                    message={message}
                    key={`tag-${id}`}
                />
            ),
        },
        {
            title: 'Bilhetes',
            dataIndex: 'tickets',
            key: 'tickets',
            width: 85,
            align: 'center' as const,
            render: (_: any, { tickets }: ITableData) => (
                <span style={{ fontWeight: 600 }}>
                    {tickets?.length || 0}
                </span>
            ),
            sorter: (a: ITableData, b: ITableData) => (a?.tickets?.length || 0) - (b?.tickets?.length || 0),
        },
        {
            title: 'Data Emissão',
            dataIndex: 'date_issue',
            key: 'date_issue',
            width: 160,
            align: 'center' as const,
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITableData, b: ITableData) => new Date(a?.date_issue!).getTime() - new Date(b?.date_issue!).getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({ dataIndex: 'date_issue' }),
            render: (data: any) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 100,
            render: (_: any, record: ITableData) => {
                const hasLink = Boolean(record.link && verifyLinkIsValid({ link: record.link }));
                const hasKey = Boolean(record.key);

                return (
                    <Space size={4}>
                        {hasLink && (
                            <TableActionButton
                                action="custom"
                                icon={<LinkOutlined />}
                                tooltip="Abrir link da nota"
                                onClick={() => window.open(normalizeLink(record.link!), '_blank')}
                            />
                        )}
                        {hasKey && (
                            <TableActionButton
                                action="custom"
                                icon={<CopyOutlined />}
                                tooltip="Copiar chave de acesso"
                                onClick={() => {
                                    navigator.clipboard.writeText(String(record.key));
                                    notification.success({
                                        message: "Chave copiada!",
                                        description: (
                                            <a href={getLinkInvoice(record.type || 'NFE')} target="_blank" rel="noopener noreferrer">
                                                Abrir link SEFAZ
                                            </a>
                                        ),
                                        duration: 3,
                                    });
                                }}
                            />
                        )}
                        {isAdmin && (
                            <TableActionButton
                                action="view"
                                tooltip="Ver detalhes da nota no painel"
                                onClick={() => navigate(`/nota/detalhes/${record.id}`)}
                            />
                        )}
                    </Space>
                );
            },
        },
    ];

    return {
        columns,
    };
}

